import React, { useCallback, useEffect } from 'react';
import { FieldDatepicker, FieldInput, FormCol, FormRow, OptionType } from 'motion-components';
import {
  FieldSelectCollector,
  FieldSelectDriver,
  FieldSelectSeries,
  FieldSelectVehicle,
  FieldSelectWasteReceptionPoint,
  FormFieldCollectionPointDlg,
  FormFieldIdocDlg,
} from 'shared-pages/components';
import { useFormikContext } from 'formik';
import { useRootStore } from 'store';
import { useHandleError } from 'shared-pages/services';
import { useTranslation } from 'react-i18next';
import CollectionExtrasTable from './collectionExtras-table';
import { isActiveField } from 'shared-pages/common/form-helpfunc';
import { Link, useHistory } from 'react-router-dom';

import { ButtonNew } from './button-new';
import { useLazyQuery } from '@apollo/client';
import {
  gqlGetCollectionPointById,
  gqlGetCollectorByCollectionPoint,
  gqlGetWastereceptionpointsByCollectorId,
} from '../graphql';
import { isFotokiklosi, isAfisCyprus } from 'shared-pages/common/constants';
import { NumberingSeries } from 'shared-pages';

/**
 * Updated FormFields to rely on the final form data from Formik.
 * We remove local logic for lines (like data.collectionLines if new) and rely on form state.
 * We do display the table if either isNew or data.
 */

const FormFields = ({ isNew, data }) => {
  // Restrict WasteReception Points to Collector's Only
  const _restrictWastereceptionpoints = isFotokiklosi ? true : false;

  const { values, setFieldValue, errors } = useFormikContext<any>();

  const { appStore } = useRootStore();
  const { t } = useTranslation();
  const { handleGraphqlError } = useHandleError('OrderFormFields');
  const history = useHistory();

  const [fetchCollectionPoint, { data: collectionPointData }] = useLazyQuery(
    gqlGetCollectionPointById,
    {
      fetchPolicy: 'network-only',
      onError: (error) => handleGraphqlError('query-get-collectionpoint', error),
    },
  );

  const [fetchCollectorByCollectionPoint, { data: collectorFromPoint }] = useLazyQuery(
    gqlGetCollectorByCollectionPoint,
    {
      fetchPolicy: 'network-only',
      onError: (error) => handleGraphqlError('query-get-collector', error),
    },
  );

  const [fetchWastereceptionpointsByCollectorId, { data: wastereceptionpointData }] = useLazyQuery(
    gqlGetWastereceptionpointsByCollectorId,
    {
      fetchPolicy: 'network-only',
      onError: (error) => handleGraphqlError('query-get-wastereceptionpoint', error),
    },
  );

  const canEdit = appStore.isPlatformOwner || appStore.isCollector || isNew;
  const canEditSeriesSection = !isNew
    ? false
    : appStore.isPlatformOwner ||
      appStore.isCollector ||
      appStore.isCollectorDriver ||
      (appStore.isWasteReceptionPoint && (isNew || data?.wasteOriginTypeId === 2));

  const updateCollector = useCallback(
    (collectorId: number) => {
      setFieldValue('collectorId', collectorId);
    },
    [setFieldValue, fetchWastereceptionpointsByCollectorId],
  );

  const onCollectorIdChange = useCallback(
    (option: OptionType) => {
      if (!option.value || values.collectorId === option.value) return;
      updateCollector(option.value);
    },
    [values.collectorId, updateCollector],
  );

  const onCollectionPointIdChange = useCallback(
    async ({ id }: { id: number | null }) => {
      if (!id || values.collectionPointId === id) return;
      setFieldValue('collectionPointId', id);
      fetchCollectionPoint({ variables: { id } });
      // Φέρε τον Συλλέκτη από το Σημείο Συλλογής μόνο αν ΔΕΝ έχει ήδη οριστεί ένας Συλλέκτης
      if (!values.collectorId) {
        fetchCollectorByCollectionPoint({ variables: { collectionPointId: id } });
      }
      if (isAfisCyprus) {
        setFieldValue('wastereceptionpointId', 1);
      }
    },
    [
      values.collectionPointId,
      setFieldValue,
      fetchCollectionPoint,
      fetchCollectorByCollectionPoint,
    ],
  );

  const onFromWastereceptionpointChange = useCallback(
    (option: OptionType) => {
      if (!option.value || values.wastereceptionpointId === option.value) return;
      setFieldValue('wastereceptionpointId', option.value);
    },
    [values.wastereceptionpointId, setFieldValue],
  );

  {
    /*} useEffect(() => {
    const wrpList = wastereceptionpointData?.getWastereceptionpointsByCollectorId;
    console.log('WRP List =>', wrpList);
    console.log('hasDefaultWrp =>', hasDefaultWrp);
    // Only proceed if we haven't set a default yet AND there's no existing value already
    if (!hasDefaultWrp && !values.wastereceptionpointId) {
      if (wrpList?.length > 0) {
        setFieldValue('wastereceptionpointId', wrpList[0].id);
      } else {
        setFieldValue('wastereceptionpointId', null);
      }
      setDefaultWrp(true);
    }
  }, [
    hasDefaultWrp,
    values.wastereceptionpointId,
    wastereceptionpointData,
    setFieldValue,
    setDefaultWrp,
  ]);
*/
  }
  useEffect(() => {
    if (collectorFromPoint?.getCollectorByCollectionPoint?.id) {
      updateCollector(collectorFromPoint.getCollectorByCollectionPoint.id);
    }
  }, [collectorFromPoint, updateCollector]);

  useEffect(() => {
    if (collectionPointData?.getCollectionpointById) {
      console.log('FormFields values =>', collectionPointData);

      const cp = collectionPointData.getCollectionpointById;
      setFieldValue('collectionpoint', {
        id: cp.id,
        name: cp.name,
        contactPerson: cp.contactPerson,
        addressLine1: cp.addressLine1,
        addressLine2: cp.addressLine2,
        addressArea: cp.addressArea,
        postalCode: cp.postalCode,
        phone: cp.phone,
        mobile: cp.mobile,
        profession: cp.profession,
      });
      setFieldValue('collectionPointId', cp.id);
      if (cp.contactPerson) setFieldValue('colpointContactPerson', cp.contactPerson);
    }
  }, [collectionPointData, setFieldValue]);

  // Δημιουργία Εντύπου Αναγνώρισης απο Συλλογή
  const handleCreateIdoc = useCallback(() => {
    history.push('/idoc/-1', {
      collectionId: values.id,
      collectionCode: values.code,
      collectionPointId: values.collectionPointId,
      collectorId: values.collectorId,
      dateCollected: values.dateCollected,
      vehicleId: values.vehicleId,
      driverId: values.driverId,
      wastereceptionpointId: values.wastereceptionpointId,
      colpointContactPerson: values.colpointContactPerson,
      finDispNote: values.finDispNote,
      collectionLine: {
        id: values.collectionLines[0]?.id,
        materialId: values.collectionLines[0]?.materialId,
        qty: values.collectionLines[0]?.qty,
        netWeight: values.collectionLines[0]?.netWeight,
        finDispNote: values.collectionLines[0]?.finDispNote,
      },
    });
  }, [history, values]);

  return (
    <>
      <div>
        {/* 1st Row */}
        <FormRow>
          <FieldSelectSeries
            as={FormCol}
            required={true}
            label={t('Σειρά Αρίθμησης Εντύπου')}
            name="seriesId"
            seriestypeId={NumberingSeries.Collections}
            disabled={!isNew}
          />
          <FormCol>
            <FieldInput
              disabled={!canEditSeriesSection}
              // as={FormCol12}
              label={t('Αριθμός Εντύπου')}
              name="codeNumber"
              type="number"
              placeholder={t('Αφήστε κενό για Αυτόματη Αρίθμηση')}
            />
          </FormCol>
          <FieldInput
            disabled={!canEditSeriesSection}
            as={FormCol}
            label={t('Κωδικός Εντύπου')}
            name="code"
          />
          <FormCol className="col-md">{!isNew && isActiveField(t, canEdit)}</FormCol>
        </FormRow>
        {/* 2nd Row */}
        <FormRow>
          <FormCol className="col-md">
            <FormFieldCollectionPointDlg
              label={t('Σημείο Συλλογής')}
              disabled={!canEdit}
              idFieldName="collectionPointId"
              nameFieldName="collectionpoint.name"
              onChange={(valueId, valueName) => {
                onCollectionPointIdChange({ id: valueId });
              }}
              required
            />
            {errors.collectionPointId && (
              <div className="text-danger text-sm">{errors.collectionPointId as any}</div>
            )}
          </FormCol>
          <FormCol />
          <FormCol className="col-md">
            <FieldSelectCollector
              name="collectorId"
              label={t('Συλλέκτης')}
              onChange={onCollectorIdChange}
              disabled={!canEdit}
              required
            />
            {errors.collectorId && (
              <div className="text-danger text-sm">{errors.collectorId as string}</div>
            )}
          </FormCol>
          <FormCol />
        </FormRow>

        {/* 3rd Row */}
        <FormRow>
          <FormCol className="col-md">
            <FieldInput
              label={t('Διεύθυνση')}
              name="collectionpoint.addressLine1"
              placeholder=""
              disabled
            />
          </FormCol>
          <FormCol className="col-md">
            <FieldInput
              label={t('Επάγγελμα')}
              name="collectionpoint.profession.name"
              placeholder=""
              disabled
            />
          </FormCol>
          <FormCol className="col-md">
            <FieldSelectWasteReceptionPoint
              disabled={!canEdit}
              onChange={onFromWastereceptionpointChange}
              name="wastereceptionpointId"
              required
              collectorId={_restrictWastereceptionpoints ? values.collectorId : undefined}
            />
          </FormCol>
          <FormCol className="col-md">
            {data?.collectionOrders?.[0] && (
              <>
                <Link to={`/order/${data.collectionOrders[0].id}`} className="d-block mb-2">
                  {t('Αίτημα')}:
                </Link>
                <FieldInput name="collectionOrders[0].code" placeholder="" disabled />
              </>
            )}
          </FormCol>
        </FormRow>

        <FormRow>
          <FormCol className="col-md">
            <FieldInput
              label={t('Διεύθυνση - 2η γραμμή')}
              name="collectionpoint.addressLine2"
              placeholder=""
              disabled
            />
          </FormCol>
          <FormCol className="col-md">
            <FieldInput
              label={t('Υπεύθυνος')}
              name="colpointContactPerson"
              placeholder=""
              disabled={!canEdit}
            />
          </FormCol>
          <FormCol className="col-md">
            <FieldDatepicker
              label={t('Ημερομηνία Συλλογής')}
              name="dateCollected"
              disabled={!canEdit}
            />
          </FormCol>
          <FormCol className="col-md">
            {data?.routeLineId && data?.routeLine?.route && (
              <>
                <Link to={`/route/${data.routeLine.route.id}`} className="d-block mb-2">
                  {t('Δρομολόγιο')}:
                </Link>
                <FieldInput name="routeLine.route.code" placeholder="" disabled />
              </>
            )}
          </FormCol>
        </FormRow>

        <FormRow>
          <FormCol className="col-md">
            <FieldInput
              label={t('Περιοχή')}
              name="collectionpoint.addressArea"
              placeholder=""
              disabled
            />
          </FormCol>
          <FormCol className="col-md">
            <FieldInput
              label={t('Tηλέφωνο1')}
              name="collectionpoint.phone"
              placeholder=""
              disabled
            />
          </FormCol>
          <FormCol className="col-md">
            {' '}
            <FieldInput
              label={t('Δελτίο Αποστολής')}
              name="finDispNote"
              placeholder=""
              disabled={!canEdit}
            />
          </FormCol>

          <FieldSelectVehicle
            required={false}
            disabled={!canEdit}
            as={FormCol}
            collectorId={values?.collectorId ?? undefined}
            name="vehicleId"
            defaultValues={data}
            label="Αρ. Κυκλ. Οχήματος"
          />
        </FormRow>

        {/* Νέα Γραμμή Προτελευταία */}
        <FormRow>
          <FormCol className="col-md">
            <FieldInput
              label={t('T.K.')}
              name="collectionpoint.postalCode"
              placeholder=""
              disabled
            />
          </FormCol>
          <FormCol className="col-md">
            <FieldInput label={t('Κινητό')} name="collectionpoint.mobile" placeholder="" disabled />
          </FormCol>{' '}
          <FormFieldIdocDlg
            as={FormCol}
            label={t('Έντυπο Αναγνώρισης')}
            idFieldName="idocs[0].id"
            nameFieldName="idocs[0].identificationDocument"
            disabled={!canEdit}
            onChange={(valueId, valueName) => {
              console.log('Selected iDoc =>', valueId, valueName);
            }}
          />
          <FieldSelectDriver
            as={FormCol}
            required={false}
            disabled={!canEdit}
            collectorId={values?.collectorId ?? undefined}
            name="driverId"
            defaultValues={data}
          />
        </FormRow>
        <FormRow>
          <FormCol></FormCol>
          <FormCol></FormCol>

          <ButtonNew
            label={t('Νέο Έντυπο Αναγνώρισης')}
            onClick={handleCreateIdoc}
            disabled={!canEdit || isNew}
            size="sm"
            style={{ width: '100% ' }}
          />
          <FormCol></FormCol>
        </FormRow>

        <div style={{ marginTop: '24px', marginBottom: '20px' }}>
          {/* Display table if isNew or if we have data from server */}
          {(isNew || data) && <CollectionExtrasTable isNew={isNew} canEdit={canEdit} />}
        </div>
      </div>
    </>
  );
};

export default FormFields;
